// RequestTransferBond.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  IconButton,
  Tabs,
  Tab,
  CircularProgress, // Import CircularProgress for the spinner
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import TransferBondLayout from '../transferbond/tbondlayout'; // Use TransferBondLayout
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StartNewRequestModal from './requestingSubComponents/StartRequestModal'; // Corrected import
import { toast } from 'react-toastify';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'; 
import Check from '@mui/icons-material/Check';
import ClosedRequestsGrid from './requestingSubComponents/closedRequestGrid';
import CancelledRequestsGrid from './requestingSubComponents/cancelledRequestsGrid';

// API call to fetch transfer bond transactions
const getTransferBonds = async (token) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(`${baseURL}Requestingtransferbond`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching transfer bonds: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching transfer bonds:', error);
    throw error;
  }
};

// API call to fetch user permissions
const getUserPermissions = async (token, agencyId, subAgentId) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(
      `${baseURL}User/permissions?agencyId=${agencyId}&subAgentId=${subAgentId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching user permissions: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    throw error;
  }
};

const RequestTransferBond = () => {
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = useState(false);
  const [groupedRows, setGroupedRows] = useState([]); // Holds grouped data
  const [loading, setLoading] = useState(true); // Loading state
  const [activeTab, setActiveTab] = useState(0); // State for managing active tab
  const [canViewPostingAgency, setCanViewPostingAgency] = useState(false); // permission state

  useEffect(() => {
    // Scrolls the window to the top when the component is mounted
    window.scrollTo(0, 0);
  }, []);
  // Fetch the transfer bonds data and update the grid
  const fetchGridData = useCallback(async () => {
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    if (!token) {
      toast.error('No authentication token found.');
      setLoading(false);
      return;
    }
  
    try {
      // Step 1: Fetch User Details to Get the SubAgentID and AgencyID
      const userDetailsResponse = await fetch(`${baseURL}user/info`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (!userDetailsResponse.ok) {
        throw new Error(`Error fetching user details: ${userDetailsResponse.statusText}`);
      }
  
      const userDetails = await userDetailsResponse.json();
      const { subAgentId, agencyId } = userDetails;
  
      // Step 2: Fetch Permissions Based on SubAgentID and AgencyID
      const permissionsResponse = await fetch(
        `${baseURL}User/${agencyId}/permissions/${subAgentId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (!permissionsResponse.ok) {
        throw new Error(`Error fetching user permissions: ${permissionsResponse.statusText}`);
      }
  
      const permissions = await permissionsResponse.json();
      console.log('Fetched Permissions:', permissions);
      const hasPermission = permissions.some(
        (perm) => perm.portalPermissionTypeID === 9 && perm.permit === true
      );
      setCanViewPostingAgency(hasPermission);
      console.log('Can View Posting Agency:', hasPermission);
      // Step 3: Fetch Transfer Bonds Data
      const transferBondsData = await getTransferBonds(token);

      // Group the data by dbaAgencyName
      const groupedData = transferBondsData.reduce((acc, bond) => {
        let agencyGroup = acc.find(group => group.dbaAgencyName === bond.dbaAgencyName);
        if (!agencyGroup) {
          agencyGroup = {
            dbaAgencyName: bond.dbaAgencyName,
            primaryContact: bond.primaryContact,
            agentId: bond.agentId,
            records: [],
          };
          acc.push(agencyGroup);
        }
        const existingRecord = agencyGroup.records.find(r => r.id === bond.transferBondTransactionID);

        if (!existingRecord) {
            const dateObj = new Date(bond.createdAt);
            const dateStr = dateObj.toLocaleDateString('en-US');
            const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

            agencyGroup.records.push({
                id: bond.transferBondTransactionID,
                createdAt: { date: dateStr, time: timeStr },
                numberOfBonds: bond.numberOfBonds,
                totalBondAmount: `$${bond.totalBondAmount.toLocaleString()}`,
                defendantName: `${bond.defendantFirstName || ''} ${bond.defendantLastName || ''}`.trim() || 'N/A',
                postingAgencyName:
                  bond.executingAgentID
                      ? bond.postingAgencyName || '-'
                      : '-',
                postingAgent: bond.hasBeenPosted
                ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%', // Vertically center by filling the cell
                    }}
                  >
                    <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
                      <b>Verification</b>
                    </Typography>
                    <Check color="success" fontSize="small" />
                  </Box>
                )
              : bond.executingAgentID
                ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center', // Horizontally center
                        width: '100%',
                        height: '100%', // Vertically center by filling the cell
                      }}
                    >
                      <Typography variant="body2" component="span" sx={{ mr: 0.5 }}>
                        <b>Agent Posting</b>
                      </Typography>
                      <EmojiPeopleIcon color="success" fontSize="small" />
                    </Box>
                  )
                : bond.numberOfQuotes >= 1
                  ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%', // Vertically center
                        }}
                      >
                        <i><b>{bond.numberOfQuotes}</b> quote(s) pending</i>
                      </Box>
                    )
                  : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%', // Vertically center
                        }}
                      >
                        <i>waiting for quotes...</i>
                      </Box>
                    ),
                county: bond.county,
                state: bond.state,
            });
        }
        return acc;
      }, []);

      setGroupedRows(groupedData);
    } catch (error) {
      toast.error('Error fetching transfer bonds');
    } finally {
      setLoading(false); // Stop loading when data is fetched or error occurs
    }
  }, []);

  // Fetch grid data on component load
  useEffect(() => {
    fetchGridData(); // Initial call
  
    const intervalId = setInterval(() => {
      fetchGridData(); // Repeated call every 15 seconds
    }, 15000); // 15,000 milliseconds = 15 seconds
  
    return () => clearInterval(intervalId); // Clear interval on unmount
  }, [fetchGridData]);

  const handleViewDetails = useCallback((id) => {
    navigate(`/view-details?ID=${id}`);
  }, [navigate]);

  const handleNewRequest = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleBack = useCallback(() => {
    navigate('/transferbond');
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue); // Update the active tab
  }, []);

  // Compute the total number of open requests
  const openRequestCount = useMemo(() => {
    return groupedRows.reduce((total, group) => total + group.records.length, 0);
  }, [groupedRows]);

  console.log('Render Posting Agency Column:', canViewPostingAgency);
  // Memoize columns to prevent re-creation on every render
  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'Ref #',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewDetails(params.row.id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value.date}</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            {params.value.time}
          </Typography>
        </Box>
      ),
    },
    { field: 'numberOfBonds', headerName: '#Bonds', width: 100, align: 'center', headerAlign: 'center' },
    { 
      field: 'totalBondAmount', 
      headerName: 'Total Bond Amount', 
      width: 150, 
      align: 'center', 
      headerAlign: 'center'
    },
    {
      field: 'defendantName', // New column
      headerName: 'Defendant Name',
      width: 150,
      align: 'center',
      headerAlign: 'center',
    },
    ...(canViewPostingAgency
      ? [
          {
              field: 'postingAgencyName',
              headerName: 'Posting Agency',
              width: 200,
              align: 'center',
              headerAlign: 'center',
              renderCell: (params) =>
                  params.row.postingAgencyName !== '-' ? (
                      <Typography
                          sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              textAlign: 'center',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              fontSize: '0.85rem',
                              lineHeight: 1.4,
                              height: '100%',
                          }}
                      >
                          {params.row.postingAgencyName}
                      </Typography>
                  ) : (
                      '-'
                  ),
          },
      ]
      : []),
    {
      field: 'postingAgent',
      headerName: 'Status',
      width: 180, // Increased width for better spacing
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value, // Renders the centered content
    },
    { field: 'county', headerName: 'County', width: 100, align: 'center', headerAlign: 'center' },
    { field: 'state', headerName: 'State', width: 80, align: 'center', headerAlign: 'center' },
  ], [canViewPostingAgency, handleViewDetails]);

  return (
    <TransferBondLayout>
      <Container maxWidth="lg" sx={{ paddingTop: 4 }}>
      <Box sx={{ padding: 0 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
            Transfer Bond Home Page
          </Typography>
        </Box>
        
        {/* Top Section with Start New Request Button and Informational Text */}
        <Paper sx={{
          padding: 2,
          mb: 2,
          borderRadius: 1,
          backgroundColor: '#f9f9f9',
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0',
        }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This screen allows you to view all your current transfer bond requests. You can track the status, view details, and start a new request if needed.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button variant="contained" color="primary" onClick={handleNewRequest}>
              Start New Request
            </Button>
          </Box>
        </Paper>

        {/* Tabs for Open, Closed, and Cancelled Requests */}
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              '& .Mui-selected': {
                backgroundColor: '#f9f9f9',
                fontWeight: 'bold',
              },
            }}
          >
            <Tab label={`Open Requests (${openRequestCount})`} />
            <Tab label="Closed Requests" />
            <Tab label="Cancelled Requests" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ p: 2 }}>
            {activeTab === 0 && (
              <Box>
                {/* Show spinner if loading */}
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                  </Box>
                ) : groupedRows.length > 0 ? (
                  groupedRows.map((agencyGroup, index) => (
                    <Box key={index} sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', mt: 2 }}>
                        {agencyGroup.dbaAgencyName + ' (' + agencyGroup.agentId + ')'}
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', mt: 2 }}>
                        {'Primary Contact: ' + agencyGroup.primaryContact}
                      </Typography>
                      <Paper sx={{ width: '100%' }}>
                        <DataGrid
                          rows={agencyGroup.records}
                          columns={columns}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 10 }, // Set initial page size to 10
                            },
                          }}
                          pageSizeOptions={[10]} // Limit rows per page options to only 10
                          pagination // Enable pagination
                          loading={loading}
                          autoHeight
                          sx={{ fontSize: '0.85rem' }}
                        />
                      </Paper>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Typography variant="body1" color="textSecondary">
                      No open requests found.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && <ClosedRequestsGrid canViewPostingAgency={canViewPostingAgency} />} {/* Closed Requests grid */}
            {activeTab === 2 && <CancelledRequestsGrid canViewPostingAgency={canViewPostingAgency}  />} {/* Cancelled Requests grid */}
          </Box>
        </Paper>

        {/* Modal for starting new requests */}
        <StartNewRequestModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onRequestSuccess={fetchGridData} // Call to update the grid after request submission
        />
      </Box>
      </Container>
    </TransferBondLayout>
  );
};

export default RequestTransferBond;
